// Every old-site account linked so far: the history. Claims made under the first rules (before
// 20 October 2026) landed as held coins and are still released or rejected here; newer ones are
// credited at once or decided on Old account approvals.
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, History } from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  OldAccountLoader,
  OldAccountSearch,
  OldPendingLine,
  type OldPending,
} from '../components/OldAccount';
import {
  confirmAction,
  EmptyState,
  ErrorNote,
  fmtNum,
  fmtUsd,
  KV,
  Mono,
  PageHeader,
  Pill,
  run,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
  UserChip,
} from '../components/ui';

interface Claim {
  legacyUserId: string;
  legacyUsername: string | null;
  balanceCoins: number;
  pendingCoins: number;
  /** null: claimed under the first rules (the hold tells how it went). */
  claimStatus: 'credited' | 'no_coins' | 'pending_review' | 'approved' | 'rejected' | null;
  claimCoins: number | null;
  oldState: string | null;
  wasBlocked: boolean;
  riskNotes: Record<string, unknown> | null;
  lifetimeEarnedCoins: number;
  lifetimeWithdrawnCoins: number;
  earningsCount: number;
  withdrawalsCount: number;
  reversalsCount: number;
  lastEarningAt: number | null;
  lastWithdrawalAt: number | null;
  claimedAt: number;
  decidedAt: number | null;
  decidedBy: string | null;
  userId: string;
  username: string;
  userStatus: string;
  holdId: string | null;
  holdStatus: string | null;
  holdCoins: number | null;
  releaseAt: number | null;
  resolvedAt: number | null;
  reasons: string[];
  oldPending: OldPending | null;
  pendingSyncNote: string | null;
}

const REASONS: Record<string, { label: string; tone: 'berry' | 'coin' | 'sun' }> = {
  blocked_on_old_site: { label: 'Blocked on the old site', tone: 'berry' },
  negative_balance: { label: 'Negative balance on the old site', tone: 'berry' },
  had_reversals: { label: 'Had reversed offers', tone: 'coin' },
  suspicious_flags: { label: 'Flagged as suspicious', tone: 'coin' },
  includes_pending_earnings: { label: 'Includes unconfirmed earnings', tone: 'sun' },
  large_balance: { label: 'Over 50,000 coins', tone: 'sun' },
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'waiting', label: 'Waiting for approval' },
  { key: 'review', label: 'Held, needs review' },
  { key: 'auto', label: 'Held, releasing on its own' },
  { key: 'released', label: 'Credited' },
  { key: 'reversed', label: 'Rejected' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

function bucket(c: Claim): Exclude<TabKey, 'all'> {
  // Pending coins from the old site that the release job left for a person.
  if (c.oldPending?.review) return 'review';
  switch (c.claimStatus) {
    case 'pending_review':
      return 'waiting';
    case 'rejected':
      return 'reversed';
    case 'credited':
    case 'approved':
    case 'no_coins':
      return 'released';
  }
  if (c.holdStatus === 'held') return c.releaseAt ? 'auto' : 'review';
  if (c.holdStatus === 'reversed') return 'reversed';
  return 'released';
}

const coinsOf = (c: Claim) => c.holdCoins ?? c.claimCoins ?? 0;

const DECIDED: Record<string, { label: string; tone: 'mint' | 'berry' | 'sun' | 'neutral' }> = {
  credited: { label: 'Credited', tone: 'mint' },
  approved: { label: 'Approved', tone: 'mint' },
  rejected: { label: 'Rejected', tone: 'berry' },
  no_coins: { label: 'No coins', tone: 'neutral' },
};

export function Legacy() {
  const qc = useQueryClient();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'all') as TabKey;
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['legacy'],
    queryFn: () =>
      api<{
        claims: Claim[];
        snapshot: { snapshot: number; coins: string | null };
        staffNames: Record<string, string>;
      }>('/legacy'),
    refetchInterval: 30_000,
  });
  const claims = data?.claims ?? [];
  const shown = tab === 'all' ? claims : claims.filter((c) => bucket(c) === tab);
  const n = (k: TabKey) => claims.filter((c) => bucket(c) === k).length;

  async function act(c: Claim, action: 'release' | 'reverse') {
    const coins = coinsOf(c);
    const reason = await confirmAction(
      action === 'release'
        ? {
            title: `Release ${fmtNum(coins)} coins to ${c.username}?`,
            body: `They become spendable right away (${fmtUsd(coins / 1000)}). This can't be undone from here.`,
            confirmLabel: 'Release coins',
            tone: 'coin',
            reason: {
              label: 'Why is this safe? (audit log only)',
              presets: [
                'Old-site history checks out',
                'Reversals were small and old',
                'Confirmed with the user',
              ],
            },
          }
        : {
            title: `Reject ${c.username}'s old balance?`,
            body: `The ${fmtNum(coins)} held coins are removed. The user sees "Old account balance not approved" in their wallet.`,
            confirmLabel: 'Reject balance',
            tone: 'danger',
            reason: {
              label: 'Why? (audit log only, the user doesn’t see this)',
              presets: [
                'Blocked on the old site for fraud',
                'Balance came from reversed offers',
                'Account appears to be a duplicate',
              ],
            },
          },
    );
    if (reason === null) return;
    await run(
      () => api(`/holds/${c.holdId}/${action}`, { body: { reason } }),
      action === 'release' ? 'Coins released.' : 'Balance rejected.',
    );
    await qc.invalidateQueries();
  }

  async function settlePending(c: Claim, action: 'release' | 'reverse') {
    const coins = c.oldPending?.review ?? 0;
    const reason = await confirmAction(
      action === 'release'
        ? {
            title: `Release ${fmtNum(coins)} pending coins to ${c.username}?`,
            body: `Coins that were still pending on the old site. They become spendable right away (${fmtUsd(coins / 1000)}).`,
            confirmLabel: 'Release coins',
            tone: 'coin',
            reason: {
              label: 'Why is this safe? (audit log only)',
              presets: [
                'Old-site history checks out',
                'Offers confirmed by the network',
                'Confirmed with the user',
              ],
            },
          }
        : {
            title: `Take back ${fmtNum(coins)} pending coins from ${c.username}?`,
            body: 'The held coins are removed. The user sees "Pending coins from your old account not approved" in their wallet.',
            confirmLabel: 'Take back',
            tone: 'danger',
            reason: {
              label: 'Why? (audit log only, the user doesn’t see this)',
              presets: [
                'Offer looks fraudulent',
                'Reversed by the network',
                'Account appears to be bought or shared',
              ],
            },
          },
    );
    if (reason === null) return;
    await run(
      () => api(`/legacy/${c.legacyUserId}/pending/${action}`, { body: { reason } }),
      action === 'release' ? 'Pending coins released.' : 'Pending coins taken back.',
    );
    await qc.invalidateQueries();
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Old accounts"
        description={
          <>
            {data ? `${fmtNum(claims.length)} old accounts linked so far. ` : ''}
            New links are credited at once or wait in{' '}
            <Link
              to="/legacy/approvals"
              className="font-semibold text-ink underline decoration-coin decoration-2 underline-offset-2"
            >
              Old account approvals
            </Link>
            . The number on each row is the old balance brought over at link. Old pending earnings
            come over separately; any that need you show “coins to decide” with the evidence inside.
          </>
        }
      />
      <OldAccountSearch />
      <Tabs
        label="Claim status"
        value={tab}
        onChange={(k) =>
          setParams((p) => (k === 'all' ? p.delete('tab') : p.set('tab', k), p), {
            replace: true,
          })
        }
        items={TABS.map((t) => ({
          key: t.key,
          label: t.label,
          count:
            t.key === 'review' || t.key === 'waiting' || t.key === 'auto' ? n(t.key) : undefined,
          hot: t.key === 'review' || t.key === 'waiting',
        }))}
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {!(error && !data) && (
        <div className="card overflow-hidden">
          {isLoading ? (
            <SkeletonRows />
          ) : shown.length === 0 ? (
            <EmptyState
              icon={History}
              title={tab === 'review' || tab === 'waiting' ? 'Nothing waiting' : 'Nothing here'}
            >
              {tab === 'review'
                ? 'Held claims from before 20 October 2026 with a warning sign, and old pending coins that need a person, land here.'
                : tab === 'waiting'
                  ? 'Links over the review threshold wait in Old account approvals.'
                  : claims.length === 0
                    ? 'Nobody has linked an old account yet.'
                    : 'Nothing in this tab right now. Try another one.'}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {shown.map((c) => {
                const isOpen = open === c.legacyUserId;
                const coins = coinsOf(c);
                const decided = c.claimStatus ? DECIDED[c.claimStatus] : undefined;
                // Old pending coins waiting for a person: decided inside the row, next to the evidence.
                const toDecide =
                  c.oldPending?.review && c.claimStatus !== 'pending_review'
                    ? c.oldPending.review
                    : 0;
                return (
                  <li key={c.legacyUserId}>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-3 px-4 py-3.5 sm:px-5 xl:grid-cols-[200px_104px_minmax(0,1fr)_auto_auto] xl:gap-x-5">
                      <div className="order-1 min-w-0">
                        <UserChip
                          id={c.userId}
                          username={c.username}
                          sub={`Old site: ${c.legacyUsername ?? c.legacyUserId}`}
                        />
                      </div>
                      <div
                        className="order-2 text-right tabular-nums"
                        title="The old balance this link brought over. Old pending coins come on top, one by one."
                      >
                        <div className="font-extrabold text-coin-ink">{fmtNum(coins)}</div>
                        <div className="text-[12px] text-stone">{fmtUsd(coins / 1000)} at link</div>
                      </div>
                      <button
                        onClick={() => setOpen(isOpen ? null : c.legacyUserId)}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Hide' : 'Show'} old-site details for ${c.username}`}
                        className="order-3 grid h-10 w-10 place-items-center rounded-full text-stone transition hover:bg-canvas hover:text-ink xl:order-5"
                      >
                        <ChevronDown
                          size={16}
                          aria-hidden="true"
                          className={`transition ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <div className="order-4 col-span-3 flex min-w-0 flex-wrap gap-1.5 xl:order-3 xl:col-span-1">
                        {toDecide > 0 && <Pill tone="sun">{fmtNum(toDecide)} coins to decide</Pill>}
                        {c.claimStatus !== null ? (
                          <Pill tone="neutral">New rules</Pill>
                        ) : c.reasons.length === 0 ? (
                          <Pill tone="mint">No warnings</Pill>
                        ) : (
                          c.reasons.map((r) => (
                            <Pill key={r} tone={REASONS[r]?.tone ?? 'sun'}>
                              {REASONS[r]?.label ?? r.replace(/_/g, ' ')}
                            </Pill>
                          ))
                        )}
                      </div>
                      <div className="order-5 col-span-3 flex flex-wrap items-center gap-2 xl:order-4 xl:col-span-1 xl:justify-end">
                        {toDecide ? (
                          isOpen ? null : (
                            <button
                              className="btn-primary h-10 px-4 py-0 text-[14px] sm:h-9 sm:text-[13px]"
                              onClick={() => setOpen(c.legacyUserId)}
                            >
                              Review
                            </button>
                          )
                        ) : c.claimStatus === 'pending_review' ? (
                          <Link
                            to="/legacy/approvals"
                            className="btn-coin h-10 px-4 py-0 text-[14px] sm:h-9 sm:text-[13px]"
                          >
                            Decide
                          </Link>
                        ) : decided ? (
                          <span className="flex items-center gap-2 text-[12px] text-stone">
                            <Pill tone={decided.tone}>{decided.label}</Pill>
                            <TimeAgo t={c.decidedAt ?? c.claimedAt} />
                            {c.decidedBy && (
                              <span>by {data?.staffNames[c.decidedBy] ?? c.decidedBy}</span>
                            )}
                          </span>
                        ) : c.holdStatus === 'held' && c.holdId && can('holds.decide') ? (
                          <>
                            {c.releaseAt && (
                              <span className="mr-1 text-[12px] text-stone">
                                Releases <TimeAgo t={c.releaseAt} />
                              </span>
                            )}
                            <button
                              className="btn-coin h-10 px-4 py-0 text-[14px] sm:h-9 sm:text-[13px]"
                              onClick={() => act(c, 'release')}
                            >
                              Release
                            </button>
                            <button
                              className="btn-ghost h-10 px-4 py-0 text-[14px] text-berry-deep hover:border-berry-deep sm:h-9 sm:text-[13px]"
                              onClick={() => act(c, 'reverse')}
                            >
                              Reject
                            </button>
                          </>
                        ) : c.holdStatus ? (
                          <span className="flex items-center gap-2 text-[12px] text-stone">
                            <StatusPill status={c.holdStatus} />
                            {c.resolvedAt && <TimeAgo t={c.resolvedAt} />}
                          </span>
                        ) : (
                          <span className="text-[12px] text-stone">No coins to move</span>
                        )}
                      </div>
                    </div>
                    {isOpen && (
                      <div className={toDecide > 0 ? 'flex flex-col-reverse' : ''}>
                        <Details c={c} />
                        <OldAccountLoader
                          legacyUserId={c.legacyUserId}
                          autoLoad={toDecide > 0}
                          decide={
                            toDecide > 0 &&
                            can('legacy.decide') && (
                              <>
                                <button
                                  className="btn-coin h-10 px-5 py-0 text-[14px]"
                                  onClick={() => settlePending(c, 'release')}
                                >
                                  Release {fmtNum(toDecide)}
                                </button>
                                <button
                                  className="btn-ghost h-10 px-5 py-0 text-[14px] text-berry-deep hover:border-berry-deep"
                                  onClick={() => settlePending(c, 'reverse')}
                                >
                                  Take back
                                </button>
                              </>
                            )
                          }
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const STATE: Record<string, string> = {
  active: 'Active',
  deleted: 'Deleted',
  banned: 'Banned',
  blocked: 'Blocked',
};

function Details({ c }: { c: Claim }) {
  const notes = c.riskNotes ?? {};
  // First-rules rows carry their counts in risk notes; newer rows in their own columns.
  const first = c.claimStatus === null;
  const reversals = first ? Number(notes.reversals ?? 0) : c.reversalsCount;
  return (
    <div className="grid gap-x-8 gap-y-5 border-t border-hairline bg-[#fbf8f3] px-4 py-4 sm:px-5 md:grid-cols-2">
      <div>
        <h3 className="mb-2.5 text-[13px] font-semibold text-stone">On the old site</h3>
        <KV
          items={[
            ['Old id', <Mono key="id">{c.legacyUserId}</Mono>],
            [
              'State',
              first ? (
                c.wasBlocked ? (
                  <b className="text-berry-deep">Blocked</b>
                ) : (
                  'Not blocked'
                )
              ) : (
                (STATE[c.oldState ?? ''] ?? '—')
              ),
            ],
            ...(first
              ? []
              : ([
                  ['Last earning', <TimeAgo key="e" t={c.lastEarningAt} />],
                  ['Last withdrawal', <TimeAgo key="w" t={c.lastWithdrawalAt} />],
                ] as [string, ReactNode][])),
            ['Earned in total', `${fmtNum(c.lifetimeEarnedCoins)} coins`],
            ['Cashed out in total', `${fmtNum(c.lifetimeWithdrawnCoins)} coins`],
          ]}
        />
      </div>
      <div>
        <h3 className="mb-2.5 text-[13px] font-semibold text-stone">Brought over at link</h3>
        <KV
          items={[
            ['Balance', `${fmtNum(c.balanceCoins)} coins`],
            ...(first
              ? ([['Unconfirmed earnings', `${fmtNum(c.pendingCoins)} coins`]] as [
                  string,
                  ReactNode,
                ][])
              : []),
            ['Offers completed', fmtNum(first ? Number(notes.earnings ?? 0) : c.earningsCount)],
            [
              'Reversed offers',
              reversals > 0 ? <b className="text-berry-deep">{fmtNum(reversals)}</b> : '0',
            ],
            ['Cash-outs', fmtNum(first ? Number(notes.withdrawals ?? 0) : c.withdrawalsCount)],
            ['Linked', <TimeAgo key="t" t={c.claimedAt} />],
            ['Account here', <StatusPill key="s" status={c.userStatus} />],
          ]}
        />
      </div>
      {(c.oldPending || c.pendingSyncNote) && (
        <div className="md:col-span-2">
          <OldPendingLine p={c.oldPending} note={c.pendingSyncNote} />
        </div>
      )}
    </div>
  );
}
